import { mergeIntoMain, multiWindow } from "@transport";

import * as store from "../lib/store";
import * as Icon from "./Icons";
import { Overlays } from "./Overlays";
import { ResizeGrips, WindowControls } from "./WindowControls";
import { Reader } from "./Reader";

/**
 * A window showing one message and nothing else.
 *
 * What a detached tab becomes. It has no sidebar, no list and no tab strip,
 * because it is not a second copy of the client — it is one message somebody
 * pulled out to keep in view. The window was opened at that message's address,
 * so the ordinary startup path has already fetched it; this only decides what
 * is drawn around it.
 *
 * # Putting it back is a menu item, not a drag
 *
 * Dropping a window onto the main window's tab strip needs to know where one
 * window is relative to another, and Wayland has no global coordinate space by
 * design — so a drop target would work on some desktops and silently not on
 * others, including the one this is developed on.
 *
 * A button is better than a gesture for a reason that outlives the platform,
 * though: somebody who was never told about it can find it. A drag affordance
 * is discoverable only by people who already suspect it exists.
 */
export function MessageWindow() {
  return (
    <>
      {/*
        Only where there is a main window to move into. A browser reaches this
        view through `#/read/...` in a tab of its own, and `mergeIntoMain`
        rejects there by design -- so the button would be a control that always
        fails, which is worse than no control and is the rule the offline pane
        and the double-click setting already follow.

        Nothing replaces it. The browser's own tab bar already closes the tab,
        and a bar offering an app-flavoured imitation of a control the window
        manager provides is noise.
      */}
      {multiWindow && (
        <div class="msgwin-head">
          <button
            class="btn"
            onClick={() => void merge()}
            title="Close this window and put the message back in a tab (Ctrl+Shift+M)"
          >
            <Icon.Back />
            Move into main window
          </button>
        </div>
      )}
      {/*
        The same reader a message *tab* shows, in the same container. That is
        what makes detaching read as moving the tab rather than opening
        something else: the message does not change shape on the way out.

        A second implementation of "show a message" would be a second place for
        the two builds -- and now the two window kinds -- to stop agreeing about
        what a message looks like.
      */}
      <div class="solo">
        <Reader />
      </div>
      {/*
        The same overlays the main view has, and from the same component so the
        two cannot answer that question differently again. Every one of them is
        reachable here: the reader has Reply, Reply-all and Forward buttons, and
        the keyboard handler is global -- `c`, `Shift+R` and `?` all fire in a
        window like this one. Before this, every one of them set a signal that
        nothing was listening to.
      */}
      {/*
        This view is also what the main window shows at `#/read/...`, so it
        needs the controls as much as the shell does -- and gets exactly one
        set, because only one of the two trees renders at a time.
      */}
      <WindowControls />
      <ResizeGrips />
      <Overlays />
    </>
  );
}

async function merge(): Promise<void> {
  try {
    await mergeIntoMain();
  } catch (e) {
    // This window is still here and still showing the message, so the failure
    // costs the move and nothing else. Said out loud because a button that
    // appears to do nothing reads as the app being broken.
    store.toast(
      `Could not move this message back — ${e instanceof Error ? e.message : String(e)}`,
      "error",
    );
  }
}
